"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.setupPlaySession = setupPlaySession;
const playSession_1 = require("./playSession");
const mystify_1 = require("./mystify");
const audio_1 = require("./audio");
const SETTINGS_KEY = 'balloons.play.settings';
const SESSION_KEY = 'balloons.play.session.v1';
function setupPlaySession(onBreak) {
    let saved = playSession_1.defaultPlaySettings;
    try {
        saved = (0, playSession_1.parsePlaySettings)(JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? 'null'));
    }
    catch { /* use defaults */ }
    const session = new playSession_1.PlaySession(saved, Date.now());
    const readSession = () => {
        try {
            session.restore(JSON.parse(localStorage.getItem(SESSION_KEY) ?? 'null'));
        }
        catch { /* storage unavailable */ }
    };
    const saveSession = () => {
        try {
            localStorage.setItem(SESSION_KEY, JSON.stringify(session.snapshot()));
        }
        catch { /* keep playing in memory */ }
    };
    readSession();
    session.tick(Date.now());
    saveSession();
    const saver = (0, mystify_1.createMystify)();
    let idleShown = false;
    const returnBalloon = document.createElement('button');
    returnBalloon.type = 'button';
    returnBalloon.className = 'cooldown-balloon';
    returnBalloon.hidden = true;
    returnBalloon.setAttribute('aria-label', 'Pop balloon to return to Balloons');
    returnBalloon.innerHTML = '<img src="/icons/objects/latex.svg" alt="" draggable="false"><span>Pop to play</span>';
    document.body.append(returnBalloon);
    const sync = () => {
        const timer = document.getElementById('play-timer');
        if (timer) {
            const now = Date.now();
            const remaining = session.phase === 'cooldown' ? session.cooldownUntil - now
                : session.phase === 'ready' ? 0
                    : session.settings.timeLimit * 60_000 - (session.startedAt === null || session.idle(now) ? 0 : now - session.startedAt);
            const seconds = Math.max(0, Math.ceil(remaining / 1000));
            timer.textContent = session.settings.enabled ? `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}` : 'Off';
            timer.setAttribute('aria-label', session.settings.enabled ? `Play time remaining: ${timer.textContent}` : 'Play timer disabled');
            timer.parentElement?.setAttribute('title', session.settings.enabled ? 'Starts with the first object; resets after an idle cooldown' : 'Play timer disabled');
        }
        const drawing = location.hash === '#/sketchpad' || location.hash === '#/matching' || location.hash === '#/puzzle';
        returnBalloon.hidden = session.phase !== 'ready' || !drawing;
        const puzzleReturn = document.getElementById('puzzle-view')?.shadowRoot?.querySelector('#puzzle-return');
        if (puzzleReturn) {
            puzzleReturn.disabled = session.phase === 'cooldown';
            const wait = Math.max(0, Math.ceil((session.cooldownUntil - Date.now()) / 1000));
            puzzleReturn.textContent = session.phase === 'cooldown' ? `Bubbles in ${Math.floor(wait / 60)}:${String(wait % 60).padStart(2, '0')}` : 'Return to bubbles';
            puzzleReturn.onclick = () => {
                readSession();
                if (session.phase === 'cooldown')
                    return;
                session.returnToPlay(Date.now());
                saveSession();
                location.hash = '/balloons';
                sync();
            };
        }
        for (const id of ['back-to-balloons', 'use-drawing']) {
            for (const host of ['sketchpad-view', 'matching-view', 'puzzle-view']) {
                const control = document.getElementById(host)?.shadowRoot?.querySelector(`#${id}`);
                if (control)
                    control.hidden = session.phase !== 'play';
            }
        }
    };
    const popReturnBalloon = () => {
        if (session.phase !== 'ready' || returnBalloon.hidden)
            return;
        (0, audio_1.unlockAudio)();
        (0, audio_1.playPopSound)('latex');
        readSession();
        if (session.phase !== 'ready')
            return;
        session.returnToPlay(Date.now());
        saveSession();
        idleShown = false;
        returnBalloon.hidden = true;
        location.hash = '/balloons';
        sync();
    };
    // Pop on contact, like objects in the main canvas. Do not require a precise
    // pointer-up inside a moving target or a synthesized touch click.
    returnBalloon.addEventListener('pointerdown', event => {
        if (event.button !== 0)
            return;
        event.preventDefault();
        event.stopPropagation();
        popReturnBalloon();
    });
    returnBalloon.addEventListener('click', popReturnBalloon); // Keyboard activation.
    const dialog = document.createElement('dialog');
    dialog.className = 'play-settings';
    dialog.setAttribute('aria-labelledby', 'play-settings-title');
    dialog.innerHTML = `<form method="dialog">
        <h2 id="play-settings-title">Play timer</h2>
        <label>Time limit <span>(minutes)</span><input name="timeLimit" type="number" min="0.1" max="240" step="0.1" required></label>
        <label>Cooldown <span>(minutes)</span><input name="cooldown" type="number" min="0.1" max="240" step="0.1" required></label>
        <label>Cooldown activity<select name="cooldownApp"><option value="sketchpad">Drawing</option><option value="matching">Letters & numbers</option><option value="puzzle">Puzzles</option><option value="alternate">Rotate drawing, letters & puzzles</option></select></label>
        <label>Status<select name="enabled"><option value="false">Disabled</option><option value="true">Enabled</option></select></label>
        <p>The timer starts with the first new object. After an idle cooldown, the next object starts a fresh session. The screensaver waits for 10 minutes without a new object, or the time limit if longer.</p>
        <div class="play-settings-actions"><button type="button" data-cancel>Cancel</button><button type="submit">Save</button></div>
    </form>`;
    document.body.append(dialog);
    const field = (name) => dialog.querySelector(`[name="${name}"]`);
    const openSettings = () => {
        field('timeLimit').value = String(session.settings.timeLimit);
        field('cooldown').value = String(session.settings.cooldown);
        field('enabled').value = String(session.settings.enabled);
        field('cooldownApp').value = session.settings.cooldownApp ?? 'sketchpad';
        if (!dialog.open)
            dialog.showModal();
    };
    dialog.querySelector('[data-cancel]').addEventListener('click', () => dialog.close());
    dialog.querySelector('form').addEventListener('submit', event => {
        event.preventDefault();
        const settings = (0, playSession_1.parsePlaySettings)({ enabled: field('enabled').value === 'true', timeLimit: Number(field('timeLimit').value), cooldown: Number(field('cooldown').value), cooldownApp: field('cooldownApp').value });
        session.configure(settings, Date.now());
        try {
            localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
        }
        catch { /* storage unavailable */ }
        saveSession();
        dialog.close();
        sync();
    });
    // Hidden from ordinary taps; hold Automatic sky for a second to configure.
    const target = document.querySelector('#background-menu [data-background="auto"]');
    target.title = 'Automatic sky · hold for play timer settings';
    let hold, pointer = null, x = 0, y = 0, suppressUntil = 0, heldOpen = false;
    const cancelHold = () => { clearTimeout(hold); hold = undefined; pointer = null; };
    target.addEventListener('pointerdown', event => {
        if (event.button !== 0 || pointer !== null)
            return;
        heldOpen = false;
        pointer = event.pointerId;
        x = event.clientX;
        y = event.clientY;
        hold = window.setTimeout(() => { heldOpen = true; suppressUntil = performance.now() + 1500; openSettings(); }, 900);
    }, true);
    target.addEventListener('pointermove', event => {
        if (event.pointerId === pointer && Math.hypot(event.clientX - x, event.clientY - y) > 12)
            cancelHold();
    }, true);
    target.addEventListener('pointerup', event => {
        const held = heldOpen;
        heldOpen = false;
        cancelHold();
        if (held) {
            suppressUntil = performance.now() + 800;
            event.preventDefault();
            event.stopImmediatePropagation();
        }
    }, true);
    for (const name of ['pointercancel', 'lostpointercapture'])
        target.addEventListener(name, cancelHold);
    target.addEventListener('pointerleave', event => { if (event.pointerType === 'mouse')
        cancelHold(); });
    target.addEventListener('click', event => {
        if (performance.now() < suppressUntil) {
            event.preventDefault();
            event.stopImmediatePropagation();
        }
    }, true);
    target.addEventListener('contextmenu', event => { event.preventDefault(); });
    target.addEventListener('keydown', event => {
        if (event.altKey && event.key === 'Enter') {
            event.preventDefault();
            openSettings();
        }
    });
    window.addEventListener('blur', cancelHold);
    window.addEventListener('pointerup', cancelHold);
    window.addEventListener('pointercancel', cancelHold);
    window.addEventListener('storage', event => {
        if (event.key !== SESSION_KEY && event.key !== null)
            return;
        readSession();
        session.tick(Date.now());
        if (session.phase !== 'play' && location.hash === '#/balloons') {
            onBreak();
            location.hash = `/${session.cooldownActivity}`;
        }
        sync();
    });
    window.setInterval(() => {
        if (document.hidden)
            return;
        readSession();
        const before = session.phase;
        const now = Date.now();
        session.tick(now);
        if (before !== session.phase)
            saveSession();
        if (session.phase !== 'play' && (before === 'play' || location.hash === '#/balloons')) {
            onBreak();
            location.hash = `/${session.cooldownActivity}`;
        }
        if (session.screensaverDue(now) && !idleShown && !dialog.open) {
            idleShown = true;
            onBreak();
            saver.show();
        }
        sync();
    }, 250);
    return {
        showScreensaver() { onBreak(); idleShown = true; saver.show(); },
        objectCreated() { readSession(); session.tick(Date.now()); session.objectCreated(Date.now()); saveSession(); idleShown = false; saver.hide(); sync(); },
        resolveView(view) {
            readSession();
            session.tick(Date.now());
            queueMicrotask(sync);
            if (session.phase !== 'play' && view === 'balloons') {
                const activity = session.cooldownActivity;
                history.replaceState(null, '', `#/${activity}`);
                return activity;
            }
            return view;
        }
    };
}
//# sourceMappingURL=playSessionUI.js.map